import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Briefcase, MapPin, Clock, Upload, ChevronRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CareersPage = () => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/careers`)
      .then(r => setPositions(r.data || []))
      .catch(() => setPositions([]))
      .finally(() => setLoading(false));
  }, []);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Resume must be 10MB or smaller.');
      return;
    }
    const ok = ['.pdf', '.doc', '.docx'].some(ext => f.name.toLowerCase().endsWith(ext));
    if (!ok) {
      toast.error('Please upload a PDF, DOC, or DOCX file.');
      return;
    }
    setResume(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!resume) { toast.error('Please attach your resume.'); return; }
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in all fields.'); return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('resume', resume);
      const params = new URLSearchParams({
        posting_id: selected.posting_id,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      }).toString();
      await axios.post(`${API_URL}/api/careers/apply?${params}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success("Application submitted! We'll be in touch.");
      setSelected(null);
      setForm({ name: '', email: '', phone: '' });
      setResume(null);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Submission failed. Please try again.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="careers-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Mechanicease" className="h-9 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-600">
            <Link to="/mechanics" className="hover:text-slate-900">Find Mechanics</Link>
            <Link to="/apply" className="hover:text-slate-900">Partner Program</Link>
            <Link to="/careers" className="text-orange-600 font-semibold">Careers</Link>
            <Link to="/login"><Button variant="outline" size="sm">Sign In</Button></Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/30 mb-4">We're Hiring</Badge>
          <h1 className="font-['Barlow'] text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Join the team building trust in auto repair.</h1>
          <p className="text-lg text-slate-300 max-w-2xl">
            We're a small, focused team in Abbotsford fixing one of the most frustrating consumer experiences out there. If that sounds like work worth doing, we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Open positions */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-['Barlow'] text-3xl font-bold text-slate-900 mb-2">Open Positions</h2>
        <p className="text-slate-600 mb-8">All roles are based in Abbotsford, BC unless noted.</p>

        {loading ? (
          <div className="flex justify-center py-12" data-testid="careers-loading">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : positions.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Briefcase className="h-10 w-10 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600 font-medium" data-testid="careers-empty">No open positions right now.</p>
              <p className="text-slate-500 text-sm mt-1">
                Interested in joining anyway? Send your resume to{' '}
                <a href="mailto:support@mechanicease.ca" className="text-orange-600 hover:underline">support@mechanicease.ca</a>
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {positions.map(p => (
              <Card key={p.posting_id} className="hover:shadow-md transition-shadow" data-testid={`posting-${p.posting_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                      <h3 className="font-bold text-xl text-slate-900 mb-2">{p.title}</h3>
                      <p className="text-slate-600 mb-3 whitespace-pre-wrap">{p.description}</p>
                      <div className="flex flex-wrap gap-3 text-sm text-slate-500">
                        <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{p.location}</span>
                        <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{p.employment_type}</span>
                      </div>
                    </div>
                    <Button
                      onClick={() => setSelected(p)}
                      className="bg-orange-500 hover:bg-orange-600"
                      data-testid={`apply-btn-${p.posting_id}`}
                    >
                      Apply <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Application modal */}
      {selected && (
        <div
          className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
          onClick={() => !submitting && setSelected(null)}
          data-testid="apply-modal"
        >
          <Card className="max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardContent className="p-6">
              <h3 className="font-bold text-xl text-slate-900 mb-1">Apply: {selected.title}</h3>
              <p className="text-sm text-slate-500 mb-5">{selected.location} · {selected.employment_type}</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="cand-name">Full Name</Label>
                  <Input
                    id="cand-name"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    required
                    data-testid="cand-name"
                  />
                </div>
                <div>
                  <Label htmlFor="cand-email">Email</Label>
                  <Input
                    id="cand-email"
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    required
                    data-testid="cand-email"
                  />
                </div>
                <div>
                  <Label htmlFor="cand-phone">Phone</Label>
                  <Input
                    id="cand-phone"
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    required
                    data-testid="cand-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="cand-resume">Resume (PDF, DOC, or DOCX — up to 10MB)</Label>
                  <label className="mt-1 flex items-center gap-2 border border-dashed border-slate-300 rounded-md p-3 cursor-pointer hover:bg-slate-50">
                    <Upload className="h-5 w-5 text-slate-500" />
                    <span className="text-sm text-slate-700">
                      {resume ? resume.name : 'Click to attach your resume'}
                    </span>
                    <input
                      id="cand-resume"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={onFileChange}
                      className="sr-only"
                      data-testid="cand-resume"
                    />
                  </label>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setSelected(null)}
                    disabled={submitting}
                    className="flex-1"
                    data-testid="cancel-apply"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-orange-500 hover:bg-orange-600"
                    data-testid="submit-apply"
                  >
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Submitting...</> : 'Submit Application'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer (slim) */}
      <footer className="bg-slate-950 py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center text-slate-500 text-sm">
          <p>© Mechanicease · <Link to="/" className="hover:text-white">Back to home</Link></p>
        </div>
      </footer>
    </div>
  );
};

export default CareersPage;
